import { useState } from 'react'
import { Button } from '../ui/Button'
import { Card, CardContent, CardHeader, CardTitle } from '../ui/Card'
import { Badge } from '../ui/Badge'
import { Input } from '../ui/Input'
import { X, Clock, User, ShoppingCart, Trash2, RotateCcw } from 'lucide-react'
import { SuspendedSale } from '../../services/cartPersistence'

interface SuspendedSalesModalProps {
  isOpen: boolean
  onClose: () => void
  suspendedSales: SuspendedSale[]
  onRecallSale: (saleId: string) => void
  onDeleteSale: (saleId: string) => void
  formatCurrency: (amount: number) => string
}

export const SuspendedSalesModal = ({
  isOpen,
  onClose,
  suspendedSales,
  onRecallSale,
  onDeleteSale,
  formatCurrency
}: SuspendedSalesModalProps) => {
  const [searchTerm, setSearchTerm] = useState('')

  if (!isOpen) return null

  const filteredSales = suspendedSales.filter(sale => {
    const searchLower = searchTerm.toLowerCase()
    return (
      sale.selectedCustomer?.name.toLowerCase().includes(searchLower) ||
      sale.selectedCustomer?.phone?.includes(searchTerm) ||
      sale.note?.toLowerCase().includes(searchLower) ||
      sale.cart.some(item => item.name.toLowerCase().includes(searchLower))
    )
  })

  const formatTimeAgo = (timestamp: number) => {
    const now = Date.now()
    const diff = now - timestamp
    const minutes = Math.floor(diff / 60000)
    const hours = Math.floor(diff / 3600000)
    const days = Math.floor(diff / 86400000)

    if (days > 0) return `${days}d ago`
    if (hours > 0) return `${hours}h ago`
    if (minutes > 0) return `${minutes}m ago`
    return 'Just now'
  }

  const getTotalValue = (sale: SuspendedSale) => {
    return sale.cart.reduce((sum, item) => sum + item.total, 0)
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-2 lg:p-4">
      <Card className="w-full max-w-4xl max-h-[95vh] lg:max-h-[90vh] flex flex-col">
        <CardHeader className="border-b p-3 lg:p-6">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2 text-lg lg:text-xl">
                <Clock className="w-4 h-4 lg:w-5 lg:h-5" />
                Suspended Sales
              </CardTitle>
              <p className="text-xs lg:text-sm text-gray-600 dark:text-gray-400 mt-1">
                {suspendedSales.length} suspended sale{suspendedSales.length !== 1 ? 's' : ''}
              </p>
            </div>
            <Button variant="ghost" size="icon" onClick={onClose} className="h-8 w-8 lg:h-10 lg:w-10">
              <X className="w-3 h-3 lg:w-4 lg:h-4" />
            </Button>
          </div>
        </CardHeader>

        <CardContent className="flex-1 overflow-hidden p-0">
          {/* Search Bar */}
          <div className="p-3 lg:p-4 border-b">
            <Input
              placeholder="Search by customer name, phone, or items..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full text-sm lg:text-base"
            />
          </div>

          {/* Sales List */}
          <div className="flex-1 overflow-auto">
            {filteredSales.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-48 lg:h-64 text-gray-500 p-4">
                <Clock className="w-8 h-8 lg:w-12 lg:h-12 mb-3 lg:mb-4 opacity-50" />
                {suspendedSales.length === 0 ? (
                  <div className="text-center">
                    <p className="font-medium text-sm lg:text-base">No suspended sales</p>
                    <p className="text-xs lg:text-sm">Suspended sales will appear here</p>
                  </div>
                ) : (
                  <div className="text-center">
                    <p className="font-medium text-sm lg:text-base">No sales found</p>
                    <p className="text-xs lg:text-sm">Try adjusting your search</p>
                  </div>
                )}
              </div>
            ) : (
              <div className="divide-y">
                {filteredSales.map((sale) => (
                  <div key={sale.id} className="p-3 lg:p-4 hover:bg-gray-50 dark:hover:bg-gray-800/50">
                    <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-3 lg:gap-4">
                      {/* Sale Info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-center gap-2 mb-2">
                          <h3 className="font-medium text-gray-900 dark:text-gray-100 text-sm lg:text-base truncate">
                            {sale.selectedCustomer?.name || 'Walk-in Customer'}
                          </h3>
                          <Badge variant="secondary" size="sm" className="text-xs">
                            {sale.cart.length} items
                          </Badge>
                          <Badge variant="outline" size="sm" className="text-xs">
                            {formatTimeAgo(sale.suspendedAt)}
                          </Badge>
                        </div>

                        {/* Customer Details */}
                        {sale.selectedCustomer?.phone && (
                          <div className="text-xs lg:text-sm text-gray-600 dark:text-gray-400 mb-2">
                            <span className="flex items-center gap-1">
                              📱 {sale.selectedCustomer.phone}
                            </span>
                          </div>
                        )}

                        {/* Items Preview */}
                        <div className="text-xs lg:text-sm text-gray-600 dark:text-gray-400 mb-2">
                          <div className="flex flex-wrap gap-1">
                            {sale.cart.slice(0, 2).map((item) => (
                              <span key={item.id} className="bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded text-xs">
                                {item.name} ({item.quantity})
                              </span>
                            ))}
                            {sale.cart.length > 2 && (
                              <span className="text-xs text-gray-500">
                                +{sale.cart.length - 2} more
                              </span>
                            )}
                          </div>
                        </div>

                        {/* Note */}
                        {sale.note && (
                          <div className="text-xs lg:text-sm text-gray-600 dark:text-gray-400 mb-2">
                            <span className="font-medium">Note:</span> {sale.note}
                          </div>
                        )}

                        {/* Total */}
                        <div className="text-base lg:text-lg font-semibold text-gray-900 dark:text-gray-100 mb-1">
                          Total: {formatCurrency(getTotalValue(sale))}
                        </div>

                        {/* Suspended By */}
                        {sale.suspendedBy && (
                          <div className="flex items-center gap-1 text-xs text-gray-500">
                            <User className="w-3 h-3" />
                            Suspended by {sale.suspendedBy}
                          </div>
                        )}
                      </div>

                      {/* Actions */}
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <Button
                          size="sm"
                          onClick={() => onRecallSale(sale.id)}
                          className="flex items-center gap-1 h-8 px-3 text-xs lg:text-sm"
                        >
                          <RotateCcw className="w-3 h-3" />
                          <span className="hidden sm:inline">Recall</span>
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            if (confirm('Delete this suspended sale?')) {
                              onDeleteSale(sale.id)
                            }
                          }}
                          className="flex items-center gap-1 text-red-600 hover:text-red-700 hover:bg-red-50 h-8 px-3 text-xs lg:text-sm"
                        >
                          <Trash2 className="w-3 h-3" />
                          <span className="hidden sm:inline">Delete</span>
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  )
}